import { getErrorMessage, isApiSuccess } from "../utils/error-handler"

/**
 * Servicio para gestionar invitaciones de usuarios
 */
export const invitationService = {
  /**
   * Enviar invitación a un email
   * @param {Object} invitationApi - Instancia del API de invitaciones
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  sendInvitation(invitationApi, email) {
    return new Promise((resolve) => {
      const request = { invitedEmail: email }
      invitationApi.sendInvitation(request, (error, data, response) => {
        if (isApiSuccess(error, response, 200)) {
          resolve({ success: true, data })
        } else {
          resolve({
            success: false,
            error: getErrorMessage(error, response, "Error al enviar la invitación"),
          })
        }
      })
    })
  },

  /**
   * Obtener invitaciones enviadas
   * @param {Object} invitationApi - Instancia del API de invitaciones
   * @returns {Promise<{success: boolean, data?: Object[], error?: string}>}
   */
  getSentInvitations(invitationApi) {
    return new Promise((resolve) => {
      invitationApi.getSentInvitations((error, data, response) => {
        if (isApiSuccess(error, response, 200)) {
          resolve({ success: true, data: Array.isArray(data) ? data : [] })
        } else {
          resolve({
            success: false,
            data: [],
            error: getErrorMessage(error, response, "Error al obtener invitaciones"),
          })
        }
      })
    })
  },

  /**
   * Aceptar invitación
   * @param {Object} invitationApi - Instancia del API de invitaciones
   * @param {string} token - Token de la invitación
   * @returns {Promise<{success: boolean, data?: Object, error?: string, statusCode?: number}>}
   */
  acceptInvitation(invitationApi, token) {
    return new Promise((resolve) => {
      const request = { token }
      invitationApi.acceptInvitation(request, (error, data, response) => {
        if (isApiSuccess(error, response, 200)) {
          resolve({ success: true, data })
        } else {
          // Extract status code for special handling (409 CONFLICT)
          const statusCode = response?.statusCode || response?.status || null
          resolve({
            success: false,
            statusCode: statusCode,
            error: getErrorMessage(error, response, "Error al aceptar la invitación"),
          })
        }
      })
    })
  },

  /**
   * Rechazar invitación
   * @param {Object} invitationApi - Instancia del API de invitaciones
   * @param {string} token - Token de la invitación
   * @returns {Promise<{success: boolean, data?: Object, error?: string, statusCode?: number}>}
   */
  rejectInvitation(invitationApi, token) {
    return new Promise((resolve) => {
      invitationApi.rejectInvitation(token, (error, data, response) => {
        if (isApiSuccess(error, response, 200)) {
          resolve({ success: true, data })
        } else {
          // Extract status code for special handling (409 CONFLICT)
          const statusCode = response?.statusCode || response?.status || null
          resolve({
            success: false,
            statusCode: statusCode,
            error: getErrorMessage(error, response, "Error al rechazar la invitación"),
          })
        }
      })
    })
  },

  /**
   * Obtener usuarios invitados (pacientes vinculados)
   * @param {Object} invitationApi - Instancia del API de invitaciones
   * @returns {Promise<{success: boolean, data?: Object[], error?: string}>}
   */
  getInvitedUsers(invitationApi) {
    return new Promise((resolve) => {
      invitationApi.getInvitedUsers((error, data, response) => {
        if (isApiSuccess(error, response, 200)) {
          resolve({ success: true, data: Array.isArray(data) ? data : [] })
        } else {
          resolve({
            success: false,
            data: [],
            error: getErrorMessage(error, response, "Error al obtener pacientes vinculados"),
          })
        }
      })
    })
  },

  /**
   * Cancelar invitación
   * @param {Object} invitationApi - Instancia del API de invitaciones
   * @param {number} id - ID de la invitación
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  cancelInvitation(invitationApi, id) {
    return new Promise((resolve) => {
      invitationApi.cancelInvitation(id, (error, _data, response) => {
        if (isApiSuccess(error, response, 204)) {
          resolve({ success: true })
        } else {
          resolve({
            success: false,
            error: getErrorMessage(error, response, "Error al cancelar la invitación"),
          })
        }
      })
    })
  },
}
